import type { ClientOutputs } from '@botpress/client'
import {
  Button,
  Card,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  showConfirmationPrompt,
} from '@bpinternal/ui-kit-next'
import { useState, type FC } from 'react'
import { cn, formatters } from '~/utils'
import { EmptyState, JSONTree } from '../../../elements'
import { DateTime } from 'luxon'
import { useNavigate, useSearch } from '@tanstack/react-router'
import EmptyStateIcon from '~/assets/programming-04.svg?react'
import { ChevronDown, ChevronsUpDown, ChevronUp, Download, EllipsisVertical, Trash2 } from 'lucide-react'
import { FileIcon } from './FileIcon'
import { FileStatusBadge } from './FileStatusBadge'
import { useDeleteFile } from '../../../hooks'
import { getCdmStudioUrl } from '~/shared'

type File = ClientOutputs['listFiles']['files'][number]

type Props = {
  className?: string
  files: File[]
  workspaceId: string
  botId: string
}

export const FilesTable = ({ className, files, workspaceId, botId, ...props }: Props) => {
  return (
    <Card {...props} className={cn(className, 'p-0')}>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-0"></TableHead>
            <TableHead>
              <ColumnFilterButton field="key" className="-m-3">
                Name
              </ColumnFilterButton>
            </TableHead>
            <TableHead className="hidden sm:table-cell">
              <ColumnFilterButton field="size" className="-m-3">
                Size
              </ColumnFilterButton>
            </TableHead>
            <TableHead className="hidden md:table-cell">
              <ColumnFilterButton field="updatedAt" className="-m-3">
                Last Modified
              </ColumnFilterButton>
            </TableHead>
            <TableHead className="text-xs">Tags</TableHead>
            <TableHead className="hidden sm:table-cell">
              <ColumnFilterButton field="status" className="-m-3">
                Status
              </ColumnFilterButton>
            </TableHead>
            <TableHead className="w-0"></TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {files.length === 0 ? (
            <TableCell colSpan={7} className="text-center">
              <EmptyState
                iconSize={6}
                icon={EmptyStateIcon}
                className="col-[1/-1] py-16"
                title="You don't have any files!"
                description="Files uploaded here will be available to your bot."
                primaryAction={
                  <Button onClick={() => window.open(getCdmStudioUrl(botId), '_blank')}>Edit in Studio</Button>
                }
              />
            </TableCell>
          ) : (
            files.map((file) => <FileRow key={file.id} file={file} botId={botId} workspaceId={workspaceId} />)
          )}
        </TableBody>
      </Table>
    </Card>
  )
}

const ColumnFilterButton = ({
  children,
  field,
  className,
}: {
  children: React.ReactNode
  field: 'key' | 'size' | 'updatedAt' | 'status'
  className?: string
}) => {
  const { sortDirection, sortField } = useSearch({ from: '/workspaces/$workspaceId/bots/$botId/files' })
  const navigate = useNavigate()
  const selected = sortField === field
  const newSortDirection = selected ? (sortDirection === 'asc' ? 'desc' : 'asc') : 'asc'
  const Icon = selected ? (sortDirection === 'asc' ? ChevronUp : ChevronDown) : ChevronsUpDown

  return (
    <Button
      variant="ghost"
      color="gray"
      size={'sm'}
      className={cn('w-fit', selected && 'font-semibold', className)}
      onClick={() =>
        navigate({
          to: '.',
          search: (prev) => ({ ...prev, sortField: field, sortDirection: newSortDirection }),
        })
      }
    >
      {children}
      <Icon />
    </Button>
  )
}

type FileRowProps = {
  file: File
  botId: string
  workspaceId: string
}
const FileRow: FC<FileRowProps> = ({ file, botId, workspaceId }) => {
  const navigate = useNavigate()
  const [tagsPopoverOpen, setTagsPopoverOpen] = useState(false)
  const [dropdpownOpen, setDropdownOpen] = useState(false)
  const tagCount = Object.keys(file.tags).length
  const isSystemFile = file.tags['system'] === 'true'
  const [expanded, setExpanded] = useState(false)
  const { mutate: deleteFile } = useDeleteFile({ botId, workspaceId })

  return (
    <>
      <TableRow className="cursor-pointer" onClick={() => setExpanded((o) => !o)}>
        <TableCell>
          <FileIcon size="2" variant={'surface'} type={file.contentType} />
        </TableCell>
        <TableCell className="max-w-52 truncate whitespace-nowrap font-medium text-neutral-950 sm:pr-8 2xl:max-w-none">
          {file.key}
        </TableCell>
        <TableCell className="hidden w-32 whitespace-nowrap text-neutral-950 sm:table-cell">
          {formatters.byte(file.size ?? 0)}
        </TableCell>
        <TableCell className="hidden w-40 whitespace-nowrap font-light text-neutral-950 md:table-cell">
          {DateTime.fromISO(file.updatedAt).toRelative()}
        </TableCell>
        <TableCell className="w-28 whitespace-nowrap">
          <Popover open={tagsPopoverOpen} onOpenChange={(v) => setTagsPopoverOpen(v)}>
            <PopoverTrigger disabled={!tagCount} asChild>
              <Button className="-ml-3" variant="ghost" size={'sm'} onClick={(e) => e.stopPropagation()}>
                {tagCount} {tagCount === 1 ? 'Tag' : 'Tags'}
              </Button>
            </PopoverTrigger>

            <PopoverContent
              onClick={(e) => e.stopPropagation()}
              sideOffset={5}
              className="w-fit max-w-80"
              side="bottom"
              align="start"
            >
              <div className="flex flex-col gap-1">
                <div className="text-sm font-semibold text-neutral-950">Filter by Tags</div>
                <div className="pb-4 text-xs text-neutral-900">Click on a tag to filter results</div>
                <div className="flex flex-wrap gap-2">
                  {Object.entries(file.tags).map(([key, value]) => (
                    <Button
                      key={key}
                      variant="secondary"
                      size="sm"
                      className="h-auto truncate rounded-full px-3 py-1 text-xs transition-transform hover:scale-105"
                      onClick={() => {
                        setTagsPopoverOpen(false)
                        void navigate({
                          to: '.',
                          search: (prev) => ({ ...prev, tags: { ...prev?.tags, [key]: value } }),
                        })
                      }}
                    >
                      <span className="mr-1.5 font-medium">{key}:</span>
                      <span className="truncate text-muted-foreground">{value}</span>
                    </Button>
                  ))}
                </div>
              </div>
            </PopoverContent>
          </Popover>
        </TableCell>
        <TableCell className="hidden w-24 sm:table-cell">
          <FileStatusBadge status={file.status} className="w-fit" />
        </TableCell>
        <TableCell>
          <DropdownMenu open={dropdpownOpen} onOpenChange={setDropdownOpen}>
            <DropdownMenuTrigger asChild>
              <Button size={'icon'} variant={'ghost'} className="p-0.5!" onClick={(e) => e.stopPropagation()}>
                <EllipsisVertical />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent>
              <DropdownMenuItem
                onClick={(e) => {
                  e.stopPropagation()
                  setTagsPopoverOpen(false)
                }}
              >
                <Download />
                <a href={file.url} target="_blank" rel="noreferrer" download>
                  Download
                </a>
              </DropdownMenuItem>
              {!isSystemFile && (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onClick={(e) => {
                      e.stopPropagation()
                      setDropdownOpen(false)
                      showConfirmationPrompt(
                        <span>
                          Are you sure you want to delete the file <strong>{file.key}</strong>?
                        </span>,
                        {
                          title: 'Delete File',
                          confirmLabel: 'Delete',
                          variant: 'danger',
                          showCancel: true,
                          cancelLabel: 'Cancel',
                        }
                      ).then(() => {
                        deleteFile({ id: file.id })
                      })
                    }}
                    className="cursor-pointer text-danger-600 hover:!bg-danger-100 hover:!text-danger-700"
                  >
                    <Trash2 />
                    Delete
                  </DropdownMenuItem>
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </TableCell>
      </TableRow>
      {expanded ? (
        <>
          <TableRow className="hover:bg-inherit">
            <TableCell colSpan={6}>
              <JSONTree hideRoot data={file} />
            </TableCell>
          </TableRow>
        </>
      ) : null}
    </>
  )
}
